import { Modal, notification, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { FaMagic } from 'react-icons/fa';
import TextArea from 'antd/lib/input/TextArea';
import { useSelector } from 'react-redux';
import Spinner from '../spinner/Spinner';
import './ModalQuestionAI.scss';
import DebouncedButton from '../Button/Button';
import CountryFlag from '../CountryFlag/CountryFlag';
import { AnswerTypeEnum, PlanFeaturesIds } from '../../constants/enums';
import DisabledFeature from '../DisabledFeature/DisabledFeature';
import AnswerTypeButton from '../AddQuestions/AnswerTypeButton/AnswerTypeButton';
import GenericToggle from '../GenericToggle/GenericToggle';
import useCreateScreening from '../../hooks/useCreateScreening';
import { t } from '../../i18n/i18n';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import { useLanguageTranslations } from '../../hooks/useLanguageTranslations';
import { getQuestionTypeButton } from '../../utils/getQuestionTypeButtons';
import { getStrategyAnswerCardByType } from '../../utils/modalQuestionAI/getStrategyCard';
import { getStrategyFormatData } from '../../utils/modalQuestionAI/getStrategyFormatData';
import { getAISuggestions } from '../../API/services/screeningService';
import { isAiInterviewStrategy } from '../../utils/testgroups';

const ModalQuestionAI = ({ answersType, testGroupABM, aiImg, handleCustomQuestion }) => {
  const [promptAI, setPromptAI] = useState('');
  const [dataAI, setDataAI] = useState([]);
  const [loadingDataAI, setLoadingDataAI] = useState(false);
  const { evaluation } = useSelector((state) => state.evaluation);
  const { testGroupLanguageList } = useLanguageTranslations();
  const [testGroupLanguage, setTestGroupLanguage] = useState(
    t(testGroupLanguageList.find((x) => x.value === evaluation?.language)?.label ?? 'es')
  );
  const {
    handleCurrentTypeQuestion,
    currentQuestionData,
    currentTypeQuestion,
    handleToggleIsTyping,
    handleSetNewScreeningSuggestion,
    handleAddSuggestionData,
    modalTestSuggestionAI,
    handleSetModalTestSuggestionAI,
    customTestGroupStrategy,
  } = useCreateScreening();
  const buttons = getQuestionTypeButton(testGroupABM, false, customTestGroupStrategy);

  const handleDelete = (id) => {
    const updatedDataAI = dataAI.filter((data) => data.id !== id);
    setDataAI(updatedDataAI);

    handleSetNewScreeningSuggestion(getStrategyFormatData(answersType, updatedDataAI));
  };

  const handleGetAIResponse = async () => {
    try {
      if (promptAI?.trim()?.length > 0) {
        setLoadingDataAI(true);
        handleToggleIsTyping(false);

        const body = {
          question: promptAI,
          answerType: answersType,
          language: testGroupLanguage,
        };

        const { answers } = await getAISuggestions(body);
        if (answers?.length > 0) {
          setDataAI(answers);
          notification.open({
            message: t('MESSAGE_SUCCESS_AI_GENERATION_TEST'),
            type: 'success',
          });
        } else {
          notification.open({
            message: t('MESSAGE_ERROR_AI_GENERATION_TEST'),
            type: 'error',
          });
        }
        setLoadingDataAI(false);
      }
    } catch (err) {
      notification.open({
        message: t('MESSAGE_ERROR_AI_GENERATION_TEST'),
        type: 'error',
        duration: 2000,
      });
      setLoadingDataAI(false);
    }
  };

  const checkDisabledButton = () => {
    if (loadingDataAI || dataAI?.length === 0) return true;

    if (!dataAI.every((test) => test?.question?.trim())) return true;

    switch (answersType) {
      case AnswerTypeEnum.CHECK:
        return dataAI.some(
          (test) =>
            !test.answers?.some((answer) => answer?.checked) ||
            test.answers?.some((answer) => !answer?.text?.trim())
        );

      case AnswerTypeEnum.WRITE:
        return dataAI.some((test) => test?.answer?.length === 0);

      case AnswerTypeEnum.FILE:
        return dataAI.some((test) => test?.types?.length === 0);

      case AnswerTypeEnum.TEXT:
        return dataAI.some((test) => !test?.maxChar);

      case AnswerTypeEnum.TEXT_AI:
        return dataAI.some((test) => !test?.maxChar || test?.concepts?.length === 0);

      default:
        return false;
    }
  };

  const handleActionToggle = () => {
    const changeIsTypingToggle = !currentQuestionData?.isTyping;

    handleToggleIsTyping(changeIsTypingToggle);
    setDataAI(
      dataAI.map((data) => ({
        ...data,
        isTyping: changeIsTypingToggle,
      }))
    );
  };

  useEffect(() => {
    if (dataAI && dataAI?.length > 0) {
      handleSetNewScreeningSuggestion(getStrategyFormatData(answersType, dataAI));
    }
  }, [dataAI, answersType]);

  useEffect(() => {
    if (dataAI?.length > 0) {
      setDataAI([]);
    }
  }, [answersType]);

  const handleSaveModalQuestion = () => {
    const disabledButton = checkDisabledButton();
    if (!disabledButton) {
      handleCustomQuestion(handleAddSuggestionData);
      setDataAI([]);
      setPromptAI('');
      setTestGroupLanguage(evaluation?.language ?? 'es');
      handleSetModalTestSuggestionAI(false);
    }
  };

  const newLocal = currentTypeQuestion === AnswerTypeEnum.TEXT_AI;
  return (
    <Modal
      open={modalTestSuggestionAI}
      onCancel={() => handleSetModalTestSuggestionAI(false)}
      width="70%"
      style={{ maxWidth: '600px' }}
      className="modal-question-ai"
      footer={null}
      centered
    >
      <div className="container-fluid d-flex flex-column gap-1" style={{ maxHeight: '75vh' }}>
        <div className="d-flex w-50 gap-2">
          {buttons.map((button, index) =>
            button.show ? (
              button.isPremiumFeature ? (
                <DisabledFeature
                  className="position-relative flex-1 w-auto py-0 d-flex flex-column align-items-end"
                  childrenClassname="position-relative w-100"
                  iconClassName="disabled-icon-add-questions"
                  featureId={PlanFeaturesIds.VIDEO_ANSWERS}
                  validateAmount={false}
                  usedAmount={0}
                >
                  <AnswerTypeButton
                    button={button}
                    index={index}
                    addClass="justify-content-center align-items-center w-10 h-10 p-2"
                    iconButton
                  />
                </DisabledFeature>
              ) : (
                <AnswerTypeButton
                  button={button}
                  index={index}
                  addClass="justify-content-center align-items-center w-10 h-10 p-2"
                  iconButton
                />
              )
            ) : null
          )}
        </div>
        <div className="col d-flex justify-content-between align-items-center flex-wrap gap-2 p-0">
          <b>{t('CREATE_QUESTION_MODAL_AI_TITLE')}</b>
          <Select value={t(testGroupLanguage)} onChange={(value) => setTestGroupLanguage(value)}>
            {testGroupLanguageList.map((x) => (
              <Select.Option key={x.iso} value={x.label}>
                <CountryFlag countryIso={x.iso} size="sm" className="mr-2" />
                {t(x.label)}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="row">
          <div className="col d-flex flex-column gap-2">
            <TextArea
              style={{ maxHeight: '300px' }}
              placeholder={t('CREATE_QUESTION_MODAL_AI_PROMT_PLACEHOLDER')}
              rows={3}
              value={promptAI}
              onChange={(e) => setPromptAI(e.target.value)}
            />
            {!isAiInterviewStrategy(customTestGroupStrategy) &&
              (answersType === AnswerTypeEnum.TEXT || answersType === AnswerTypeEnum.TEXT_AI) && (
                <div className="d-flex align-items-center gap-2">
                  <span className="ai-image-container d-flex align-items-center gap-1">
                    {aiImg}
                    {t('QUESTION_TEXT_TOGGLE_AI_CORRECTION')}
                  </span>
                  <GenericToggle
                    actionFunction={() =>
                      handleCurrentTypeQuestion(
                        currentTypeQuestion !== AnswerTypeEnum.TEXT_AI
                          ? AnswerTypeEnum.TEXT_AI
                          : AnswerTypeEnum.TEXT
                      )
                    }
                    defaultActive={newLocal}
                  />
                </div>
              )}
            <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
              <DebouncedButton
                type="primary"
                className={`rounded-0 d-flex align-items-center justify-content-center px-3 py-2 text-xs font-weight-600 text-light ${
                  promptAI?.trim()?.length === 0
                    ? 'disabled-button not-allowed-button'
                    : 'bg-maya-blue'
                }`}
                onClick={handleGetAIResponse}
              >
                <span className="d-flex align-items-center gap-1 text-white">
                  {t('CREATE_QUESTION_MODAL_AI_GENERATE_TEXT_BUTTON')} <FaMagic />
                </span>
              </DebouncedButton>
              <DebouncedButton
                type="primary"
                className={`rounded-0 d-flex align-items-center justify-content-center px-3 py-2 text-xs font-weight-600 text-light ${
                  checkDisabledButton() ? 'disabled-button not-allowed-button' : 'bg-maya-blue'
                }`}
                onClick={handleSaveModalQuestion}
              >
                <span className="d-flex align-items-center gap-1 text-white">
                  {t('CREATE_QUESTION_MODAL_AI_SAVE_TEXT_BUTTON')}
                </span>
              </DebouncedButton>
            </div>
            {answersType === AnswerTypeEnum.WRITE && dataAI?.length > 0 && (
              <>
                <div className="d-flex py-2">
                  <span className="text-xs">{t('QUESTION_TEXT_TOGGLE_TYPING')}</span>
                  <GenericToggle
                    actionFunction={handleActionToggle}
                    defaultActive={currentQuestionData?.isTyping}
                  />
                </div>
                <div className="d-flex align-items-center text-xs italic ml-2">
                  {t('ADD_WRITE_QUESTION_NOTES')}
                  <InfoTooltip
                    placement="top"
                    content={
                      <div className="d-flex flex-column py-2">
                        <p className="text-xs  mb-2">
                          {currentQuestionData?.isTyping
                            ? t('ADD_WRITE_QUESTION_TOOLTIP_CORRECTION_TYPING')
                            : t('ADD_WRITE_QUESTION_TOOLTIP_CORRECTION')}
                        </p>
                      </div>
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="row overflow-auto flex-grow-1">
          <div className="col d-flex flex-column gap-3 modal-scrollable-questions pr-2">
            {loadingDataAI ? (
              <Spinner show />
            ) : (
              dataAI?.map((data, index) =>
                getStrategyAnswerCardByType({
                  type: answersType,
                  data,
                  index,
                  handleDelete,
                  dataAI,
                  setDataAI,
                  strategy: customTestGroupStrategy,
                })
              )
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalQuestionAI;
